import {
  Card,
  Flex,
  Heading,
  HStack,
  SimpleGrid,
  Skeleton,
  Spacer,
  Text,
  VStack,
} from "@chakra-ui/react";
import { formatEther } from "viem";
import { useAccount, useBalance } from "wagmi";

import { RoundedButton } from "@/components/rounded-button";
import { plumeMainnetId } from "@/constants/shared";
import { useAuth } from "@/hooks/useAuth";
import { useWalletBalanceInfo } from "@/hooks/useWalletBalancesInfo";
import { formatNumberToLocale } from "@/utils";

const PlumeAssetCard = ({
  title,
  description,
  learnMoreLink,
  amountHeld,
  tokenIcon,
  tokenSymbol,
  isLoadingAmountHeld = false,
}: {
  title: string;
  description: string;
  learnMoreLink: string;
  amountHeld: number;
  tokenIcon: React.ReactNode;
  tokenSymbol: string;
  isLoadingAmountHeld?: boolean;
}) => {
  const { isAuthenticated } = useAuth();
  const { chain } = useAccount();

  // when not on plume mainnet, don't show the amount held b/c it shows native balance on other chain
  const shouldShowAmount =
    tokenSymbol === "PLUME"
      ? isAuthenticated && chain?.id === plumeMainnetId
      : isAuthenticated;

  return (
    <Card.Root rounded="16px" bg="bg" p={[6, 6, 8]} w="full" h={200}>
      <Card.Body p={0}>
        <VStack alignItems="flex-start" h="full">
          <VStack gap={1} alignItems="flex-start">
            <Heading
              fontSize="xl"
              fontWeight="700"
              lineHeight="28px"
              letterSpacing="-0.47px"
            >
              {title}
            </Heading>
            <Text
              fontWeight="500"
              lineHeight="24px"
              letterSpacing="-0.18px"
              color="fg.muted"
            >
              {description}
            </Text>
          </VStack>
          <Spacer flex={1} />
          <HStack justifyContent="space-between" w="full">
            {shouldShowAmount ? (
              <>
                {isLoadingAmountHeld ? (
                  <Skeleton w="100px" h="24px" />
                ) : (
                  <HStack gap={2}>
                    {tokenIcon}
                    <Text
                      fontWeight="500"
                      lineHeight="24px"
                      letterSpacing="-0.18px"
                    >
                      {formatNumberToLocale(amountHeld)} {tokenSymbol}
                    </Text>
                  </HStack>
                )}
              </>
            ) : (
              <HStack gap={2}>
                {tokenIcon}
                <Text
                  fontWeight="500"
                  lineHeight="24px"
                  letterSpacing="-0.18px"
                >
                  {tokenSymbol}
                </Text>
              </HStack>
            )}
            <RoundedButton
              variant="outline"
              onClick={() =>
                window.open(learnMoreLink, "_blank", "noopener,noreferrer")
              }
              ml="auto"
            >
              Learn more
            </RoundedButton>
          </HStack>
        </VStack>
      </Card.Body>
    </Card.Root>
  );
};

export const PlumeAssetsInfo = () => {
  const { address } = useAuth();
  const { data: balancePlumeToken, isLoading: isLoadingBalancePlumeToken } =
    useBalance({
      address: address ?? undefined,
    });
  const { walletTokenBalanceInfoArr, isLoading: isLoadingBalance } =
    useWalletBalanceInfo(address ?? "");
  const plumeUsdTokenBalance = walletTokenBalanceInfoArr.find(
    (token) =>
      token.token.address === "0xdddd73f5df1f0dc31373357beac77545dc5a6f3f",
  );
  const plumeEthTokenBalance = walletTokenBalanceInfoArr.find(
    (token) =>
      token.token.address === "0x39d1F90eF89C52dDA276194E9a832b484ee45574",
  );

  return (
    <VStack gap={5} w="full" alignItems="flex-start">
      <Heading
        fontSize="2xl"
        fontWeight="700"
        lineHeight="32px"
        letterSpacing="-0.47px"
      >
        Plume Native Assets
      </Heading>
      <SimpleGrid
        gap={5}
        w="full"
        alignItems="flex-start"
        columns={[1, 1, 2, 2, 3]}
      >
        <PlumeAssetCard
          title="Plume gas & governance token"
          description="Plume's official gas and governance token"
          learnMoreLink="https://docs.plume.org/plume/tokens/plume-usdplume-token"
          amountHeld={Number(
            formatEther(balancePlumeToken?.value ?? BigInt(0)),
          )}
          isLoadingAmountHeld={isLoadingBalancePlumeToken}
          tokenIcon={
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
            >
              <rect width="20" height="20" rx="10" fill="#FF3D00" />
              <path
                d="M12.1453 8.1803V11.7696L10.7363 10.3489V6.75959L12.1453 8.1803Z"
                fill="white"
              />
              <path
                d="M8.2171 12.1431H11.775L10.3667 10.7217H6.80883L8.2171 12.1431Z"
                fill="white"
              />
              <path
                d="M14.1479 14.1759V10.2005L12.7389 8.77984V12.7552L8.81142 12.7419L10.2197 14.1634L14.1471 14.1766"
                fill="white"
              />
              <path
                d="M5 5.00073L5 8.97608L6.409 10.3968L6.409 6.42143L10.3364 6.4347L8.92818 5.01327L5.00075 5"
                fill="white"
              />
              <path
                d="M14.5204 14.9309L13.721 14.1244L14.1093 13.7327L14.9086 14.5392L15 15L14.5204 14.9309Z"
                fill="white"
              />
            </svg>
          }
          tokenSymbol="PLUME"
        />
        <PlumeAssetCard
          title="Plume USD"
          description="Plume's canonical stablecoin, backed by top tier assets"
          learnMoreLink="https://docs.plume.org/plume/tokens/plume-usd"
          amountHeld={plumeUsdTokenBalance?.holdings?.tokenBalance ?? 0}
          tokenIcon={
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
            >
              <rect width="20" height="20" rx="10" fill="#FF3D00" />
              <path
                d="M14.375 12.4267C14.375 13.3342 14.0284 14.0958 13.3351 14.7116C12.6472 15.322 11.7671 15.6785 10.6946 15.7812V16.875H9.56534V15.7812C8.82331 15.7163 8.10024 15.5273 7.39612 15.214C6.69742 14.9007 6.10705 14.5037 5.625 14.0229L6.75429 12.4429C7.13885 12.794 7.57757 13.0938 8.07045 13.3423C8.56333 13.5908 9.06163 13.7501 9.56534 13.8203V10.9034C9.0508 10.763 8.5877 10.5955 8.17607 10.4011C7.76985 10.2012 7.40154 9.96354 7.07115 9.68805C6.74075 9.41257 6.48619 9.08576 6.30745 8.70765C6.12871 8.32953 6.03935 7.9163 6.03935 7.46796C6.03935 6.58748 6.35891 5.85555 6.99803 5.27217C7.64256 4.68339 8.49834 4.33768 9.56534 4.23505V3.125H10.6946V4.21884C11.35 4.28366 11.9512 4.44031 12.4983 4.68879C13.0507 4.93727 13.5084 5.24516 13.8713 5.61248L12.807 7.18437C12.5687 6.94669 12.2599 6.73873 11.8808 6.56047C11.5017 6.37682 11.1063 6.25258 10.6946 6.18776V8.97503C10.9871 9.05065 11.2688 9.13708 11.5396 9.23431C11.8158 9.32614 12.1354 9.46928 12.4983 9.66374C12.8611 9.8582 13.1726 10.0743 13.4326 10.3119C13.6925 10.5442 13.9146 10.8467 14.0988 11.2194C14.2829 11.5867 14.375 11.9892 14.375 12.4267ZM8.26544 7.40314C8.26544 7.9325 8.69874 8.35924 9.56534 8.68334V6.20396C9.16996 6.27959 8.8531 6.42543 8.61479 6.6415C8.38189 6.85216 8.26544 7.10604 8.26544 7.40314ZM10.6946 13.8041C11.1225 13.7393 11.4665 13.5962 11.7264 13.3747C11.9918 13.1478 12.1245 12.8615 12.1245 12.5158C12.1245 12.1971 11.9945 11.9352 11.7346 11.7299C11.48 11.5246 11.1334 11.3491 10.6946 11.2032V13.8041Z"
                fill="white"
              />
            </svg>
          }
          tokenSymbol="pUSD"
          isLoadingAmountHeld={isLoadingBalance}
        />
        <PlumeAssetCard
          title="Plume ETH"
          description="Plume's native ETH LST solution"
          learnMoreLink="https://peth.plume.org/"
          amountHeld={plumeEthTokenBalance?.holdings?.tokenBalance ?? 0}
          tokenIcon={
            <Flex
              justifyContent="center"
              alignItems="center"
              w={5}
              h={5}
              rounded="full"
              bg="#FF3D00"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="10"
                height="16"
                viewBox="0 0 10 16"
                fill="none"
              >
                <path
                  d="M4.99863 0.5V6.0469L9.68474 8.13931L4.99863 0.5Z"
                  fill="white"
                  fillOpacity="0.8"
                />
                <path
                  d="M4.99865 0.5L0.312531 8.13931L4.99865 6.04663V0.5Z"
                  fill="white"
                />
                <path
                  d="M4.99857 11.7321L4.99857 15.4999L9.68747 9.01196L4.99857 11.7321Z"
                  fill="white"
                  fillOpacity="0.6"
                />
                <path
                  d="M4.99862 15.4999V11.7321L0.3125 9.01196L4.99862 15.4999Z"
                  fill="white"
                />
                <path
                  d="M4.99857 10.861L9.68461 8.13955L4.99857 6.04688V10.861Z"
                  fill="white"
                  fillOpacity="0.4"
                />
                <path
                  d="M0.312531 8.13955L4.99857 10.861V6.04688L0.312531 8.13955Z"
                  fill="white"
                  fillOpacity="0.7"
                />
              </svg>
            </Flex>
          }
          tokenSymbol="pETH"
          isLoadingAmountHeld={isLoadingBalance}
        />
      </SimpleGrid>
    </VStack>
  );
};
